import React, { useState, useRef, useEffect } from 'react';

const TextAreaWithPlaceholders = ({ 
    id,
    value = '', 
    onChange,
    placeholder = '',
    placeholders = {},
    placeholderDisplayMap = {},
    style = {},
    className = '',
    ...props 
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredPlaceholders, setFilteredPlaceholders] = useState([]);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [slashPosition, setSlashPosition] = useState(null); // Track where the slash was typed
    
    const divRef = useRef(null);
    const dropdownRef = useRef(null);

    // Use placeholderDisplayMap if provided, otherwise fall back to placeholders
    const sourceMap = Object.keys(placeholderDisplayMap).length > 0 ? placeholderDisplayMap : placeholders;
    
    // Flatten nested placeholders: use ONLY leaf key names (no section prefixes)
    const flatPlaceholderMap = {};
    const flattenPlaceholders = (obj) => {
        Object.keys(obj).forEach(key => {
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                flattenPlaceholders(obj[key]);
            } else {
                flatPlaceholderMap[key] = obj[key];
            }
        });
    };
    flattenPlaceholders(sourceMap);

    // Convert text with placeholders to JSX elements
    const renderContent = (text) => {
        if (!text) {
            return <span style={{ color: '#999' }}>{placeholder}</span>;
        }

        const placeholderRegex = /\{([a-zA-Z0-9_.]+)\}/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        let badgeCount = 0;
        while ((match = placeholderRegex.exec(text)) !== null) {
            // Add text before the placeholder
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }

            // Add the placeholder as a span
            const key = match[1];
            const placeholderText = match[0]; // Capture the placeholder text immediately
            const displayText = flatPlaceholderMap[key] 
            const currentBadgeIndex = badgeCount; // Capture the current badge index
            
            parts.push(
                <span
                    key={`placeholder-${match.index}`}
                    data-placeholder={placeholderText}
                    data-key={key}
                    style={{
                        display: 'inline-block',
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        padding: '2px 6px',
                        margin: '0 1px',
                        borderRadius: '3px',
                        fontSize: '0.875rem',
                        border: '1px solid #bbdefb',
                        cursor: 'pointer'
                    }}
                    onClick={() => removePlaceholder(placeholderText, currentBadgeIndex)}
                    title={`Click to remove ${key}`}
                    contentEditable={false}
                >
                    {displayText}
                    <span style={{ marginLeft: '4px', fontSize: '0.75rem' }}>×</span>
                </span>
            );

            lastIndex = match.index + match[0].length;
            badgeCount++;
        }

        // Add remaining text
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        return parts;
    };

    // Remove a placeholder from the text
    const removePlaceholder = (placeholderText, badgeId = null) => {
        if (badgeId) {
            // Remove by specific badge ID (from renderContent)
            const placeholderRegex = /\{([a-zA-Z0-9_.]+)\}/g;
            let match;
            let count = 0;
            let startPos = -1;
            
            while ((match = placeholderRegex.exec(value)) !== null) {
                if (match[0] === placeholderText) {
                    if (count === badgeId) {
                        startPos = match.index;
                        break;
                    }
                    count++;
                }
            }
            
            if (startPos !== -1) {
                const newValue = value.substring(0, startPos) + value.substring(startPos + placeholderText.length);
                if (onChange) {
                    onChange(id, (newValue || '') + ' ');
                }
            }
        } else {
            // Fallback: remove first occurrence (for updateExistingBadges)
            const newValue = value.replace(placeholderText, '');
            if (onChange) {
                onChange(id, (newValue || '') + ' ');
            }
        }
    };

    // Save cursor position - count only text content, not badge content
    const saveCursorPosition = () => {
        const selection = window.getSelection();
        if (selection.rangeCount === 0) return null;
        
        const range = selection.getRangeAt(0);
        let caretOffset = 0;
        
        // Walk through all nodes before the cursor position
        const walker = document.createTreeWalker(
            divRef.current,
            NodeFilter.SHOW_ALL,
            null,
            false
        );
        
        let node;
        while (node = walker.nextNode()) {
            if (node === range.startContainer) {
                // We've reached the container with the cursor
                if (node.nodeType === Node.TEXT_NODE) {
                    caretOffset += range.startOffset;
                }
                break;
            }
            
            if (node.nodeType === Node.TEXT_NODE) {
                // Skip text nodes inside placeholder spans
                let parent = node.parentNode;
                let inPlaceholder = false;
                while (parent && parent !== divRef.current) {
                    if (parent.hasAttribute && parent.hasAttribute('data-placeholder')) {
                        inPlaceholder = true;
                        break;
                    }
                    parent = parent.parentNode;
                }
                
                if (!inPlaceholder) {
                    caretOffset += node.textContent.length;
                }
            } else if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-placeholder')) {
                // For placeholder spans, count the placeholder text length, not display text
                const placeholderText = node.getAttribute('data-placeholder');
                caretOffset += placeholderText.length;
            }
        }
        
        return caretOffset;
    };

    // Restore cursor position accurately but safely
    const restoreCursorPosition = (caretOffset) => {
        if (caretOffset === null || !divRef.current) return;
        
        try {
            const selection = window.getSelection();
            const range = document.createRange();
            
            let charCount = 0;
            let targetNode = null;
            let targetOffset = 0;
            let found = false;
            
            // Walk through all nodes, counting logical text length
            const walker = document.createTreeWalker(
                divRef.current,
                NodeFilter.SHOW_ALL,
                null,
                false
            );
            
            let node;
            while (node = walker.nextNode()) {
                if (node.nodeType === Node.TEXT_NODE) {
                    // Skip text nodes inside placeholder spans
                    let parent = node.parentNode;
                    let inPlaceholder = false;
                    while (parent && parent !== divRef.current) {
                        if (parent.hasAttribute && parent.hasAttribute('data-placeholder')) {
                            inPlaceholder = true;
                            break;
                        }
                        parent = parent.parentNode;
                    }
                    
                    if (!inPlaceholder) {
                        const nodeLength = node.textContent.length;
                        
                        if (caretOffset <= charCount + nodeLength) {
                            // Found the target node
                            targetNode = node;
                            targetOffset = Math.max(0, Math.min(caretOffset - charCount, nodeLength));
                            found = true;
                            break;
                        }
                        
                        charCount += nodeLength;
                    }
                } else if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-placeholder')) {
                    // For placeholder spans, count the placeholder text length
                    const placeholderText = node.getAttribute('data-placeholder');
                    const placeholderLength = placeholderText.length;
                    
                    if (caretOffset <= charCount + placeholderLength) {
                        // Cursor is within this placeholder span
                        // Place cursor after the badge
                        range.setStartAfter(node);
                        range.setEndAfter(node);
                        found = true;
                        break;
                    }
                    
                    charCount += placeholderLength;
                }
            }
            
            if (found) {
                if (targetNode) {
                    range.setStart(targetNode, targetOffset);
                    range.setEnd(targetNode, targetOffset);
                }
                // If we found position after a badge, range is already set above
            } else {
                // Fallback: place at end of last text node
                const walker2 = document.createTreeWalker(
                    divRef.current,
                    NodeFilter.SHOW_TEXT,
                    {
                        acceptNode: (node) => {
                            let parent = node.parentNode;
                            while (parent && parent !== divRef.current) {
                                if (parent.hasAttribute && parent.hasAttribute('data-placeholder')) {
                                    return NodeFilter.FILTER_REJECT;
                                }
                                parent = parent.parentNode;
                            }
                            return NodeFilter.FILTER_ACCEPT;
                        }
                    },
                    false
                );
                
                let lastNode = null;
                while (node = walker2.nextNode()) {
                    lastNode = node;
                }
                
                if (lastNode) {
                    range.setStart(lastNode, lastNode.textContent.length);
                    range.setEnd(lastNode, lastNode.textContent.length);
                } else {
                    range.selectNodeContents(divRef.current);
                    range.collapse(false);
                }
            }
            
            selection.removeAllRanges();
            selection.addRange(range);
        } catch (error) {
            console.warn('Cursor restoration failed:', error);
        }
    };

    // Extract text content while preserving placeholder positions
    const extractTextWithPlaceholders = () => {
        if (!divRef.current) return '';
        
        let result = '';
        
        // Simple approach: iterate through direct children of the contentEditable div
        const children = Array.from(divRef.current.childNodes);
        
        children.forEach(child => {
            if (child.nodeType === Node.TEXT_NODE) {
                // Regular text node
                result += child.textContent;
            } else if (child.nodeType === Node.ELEMENT_NODE && child.hasAttribute('data-placeholder')) {
                // Placeholder span - use the original placeholder text
                result += child.getAttribute('data-placeholder');
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                // Other element - get its text content (might be broken placeholder spans)
                result += child.textContent || '';
            }
        });
        
        return result;
    };

    // Update existing badges without rebuilding the entire content
    const updateExistingBadges = () => {
        if (!divRef.current) return;
        
        // Find all placeholder spans and update them if needed
        const placeholderSpans = divRef.current.querySelectorAll('[data-placeholder]');
        placeholderSpans.forEach(span => {
            const key = span.getAttribute('data-key');
            const newDisplayText = flatPlaceholderMap[key] || 'Value';
            
            // Update the display text if it has changed
            const textNode = span.firstChild;
            if (textNode && textNode.nodeType === Node.TEXT_NODE) {
                if (textNode.textContent !== newDisplayText) {
                    textNode.textContent = newDisplayText;
                }
            }
            
            // Ensure the click handler is attached
            span.onclick = () => removePlaceholder(span.getAttribute('data-placeholder'));
        });
    };

    // Store the previous content to detect what actually changed
    const previousContentRef = useRef('');



    // Handle keyboard input to manually manage content
    const handleBeforeInput = (e) => {
        // Don't prevent default, but store the state before input
        previousContentRef.current = extractTextWithPlaceholders();
    };

    // Helper to emit value with a guaranteed trailing space
    const emitValue = (text) => {
        const out = (text || '') + ' ';
        if (onChange) {
            try { console.debug('TextAreaWithPlaceholders emit:', JSON.stringify(out)); } catch (_) {}
            onChange(id, out);
        }
    };

    // Handle text input
    const handleInput = (e) => {
        // Skip if we're already updating programmatically
        if (isUpdatingRef.current) return;
        
        isUpdatingRef.current = true;
        const caretOffset = saveCursorPosition();
        
        // Extract the current text with placeholders preserved
        const newValue = extractTextWithPlaceholders();
        

        
        // Update the logical content
        previousContentRef.current = newValue;
        
        emitValue(newValue);
        
        // Just update badges and restore cursor
        setTimeout(() => {
            updateExistingBadges();
            restoreCursorPosition(caretOffset);
            isUpdatingRef.current = false;
        }, 0);

        // Check for slash to show dropdown
        const currentSelection = window.getSelection();
        if (currentSelection.rangeCount > 0) {
            const range = currentSelection.getRangeAt(0);
            const textNode = range.startContainer;
            
            if (textNode.nodeType === Node.TEXT_NODE) {
                const textBeforeCursor = textNode.textContent.substring(0, range.startOffset);
                const lastSlashIndex = textBeforeCursor.lastIndexOf('/');
                
                if (lastSlashIndex !== -1) {
                    const searchText = textBeforeCursor.substring(lastSlashIndex + 1);
                    setSearchTerm(searchText);
                    
                    // Filter placeholders
                    let filtered;
                    if (searchText === '') {
                        filtered = Object.keys(flatPlaceholderMap);
                    } else {
                        filtered = Object.keys(flatPlaceholderMap).filter(key =>
                            key.toLowerCase().includes(searchText.toLowerCase()) ||
                            flatPlaceholderMap[key].toString().toLowerCase().includes(searchText.toLowerCase())
                        );
                    }
                    
                    setFilteredPlaceholders(filtered);
                    
                    if (filtered.length > 0) {
                        // Calculate dropdown position
                        const rect = range.getBoundingClientRect();
                        const containerRect = divRef.current.getBoundingClientRect();
                        
                        setDropdownPosition({
                            top: rect.bottom - containerRect.top + 5,
                            left: rect.left - containerRect.left
                        });
                        
                        // Save the slash position for later use
                        const currentText = extractTextWithPlaceholders();
                        const textBeforeCursor = currentText.substring(0, caretOffset);
                        const lastSlashIndex = textBeforeCursor.lastIndexOf('/');
                        setSlashPosition(lastSlashIndex);
                        
                        setShowDropdown(true);
                    } else {
                        setShowDropdown(false);
                    }
                } else {
                    setShowDropdown(false);
                }
            }
        }
    };

    // Handle placeholder selection
    const handlePlaceholderSelect = (placeholderKey) => {
        const selection = window.getSelection();
        if (selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const textNode = range.startContainer;
        
        // If cursor is inside a badge, we need to find the actual text position
        let actualTextNode = textNode;
        let actualOffset = range.startOffset;
        
        if (textNode.nodeType === Node.TEXT_NODE) {
            // Check if this text node is inside a placeholder span
            let parent = textNode.parentNode;
            let isInsideBadge = false;
            let badgeSpan = null;
            let isInsideDropdown = false;
            
            while (parent && parent !== divRef.current) {               
                // Check if we're inside the dropdown (look for dropdown styling)
                if (parent.style && (
                    parent.style.position === 'absolute' || 
                    parent.style.zIndex === '1000' ||
                    (parent.style.backgroundColor && parent.style.backgroundColor.includes('white'))
                )) {
                    isInsideDropdown = true;
                }
                
                if (parent.hasAttribute && parent.hasAttribute('data-placeholder')) {
                    isInsideBadge = true;
                    badgeSpan = parent;
                    break;
                }
                parent = parent.parentNode;
            }
            
            // If we're not inside a badge and we're inside the dropdown, handle it
            if (!isInsideBadge && isInsideDropdown) {
                // Use the saved slash position to insert the placeholder
                const currentText = extractTextWithPlaceholders();
                
                if (slashPosition !== null && slashPosition !== -1) {
                    // Find the end of the slash command
                    let commandEnd = slashPosition + 1;
                    for (let i = slashPosition + 1; i < currentText.length; i++) {
                        if (currentText[i] === ' ' || currentText[i] === '/' || currentText[i] === '\n') {
                            commandEnd = i;
                            break;
                        }
                    }
                    
                    // Replace the slash command with the placeholder
                    const beforeSlash = currentText.substring(0, slashPosition);
                    const afterCommand = currentText.substring(commandEnd);
                    const placeholder = `{${placeholderKey}}`;
                    
                    // Add a space after the placeholder if it's at the end of the text
                    const isAtEnd = commandEnd >= currentText.length;
                    const newValue = beforeSlash + placeholder + (isAtEnd ? ' ' : '') + afterCommand;                    
                    emitValue(newValue);
                } else {
                    // No slash found, append at the end
                    const newValue = currentText + `{${placeholderKey}}`;
                    emitValue(newValue);
                }
                
                setShowDropdown(false);
                setSlashPosition(null); // Clear the saved position
                return;
            }
            
            if (isInsideBadge) {
                // We need to find the position in the actual text
                // Get the current logical text and find where this badge is
                const currentText = extractTextWithPlaceholders();
                const placeholderText = badgeSpan.getAttribute('data-placeholder');
                
                // Find the position of this placeholder in the logical text
                const placeholderIndex = currentText.indexOf(placeholderText);
                
                if (placeholderIndex !== -1) {
                    // Insert the new placeholder after this one
                    const beforePlaceholder = currentText.substring(0, placeholderIndex + placeholderText.length);
                    const afterPlaceholder = currentText.substring(placeholderIndex + placeholderText.length);
                    const newValue = beforePlaceholder + `{${placeholderKey}}` + afterPlaceholder;
                    
                    emitValue(newValue);
                } else {
                    // Fallback: append at the end
                    const newValue = currentText + `{${placeholderKey}}`;
                    emitValue(newValue);
                }
                
                setShowDropdown(false);
                return;
            }
            
            // Cursor is in a regular text node
            const textBeforeCursor = textNode.textContent.substring(0, range.startOffset);
            const lastSlashIndex = textBeforeCursor.lastIndexOf('/');
                        
            if (lastSlashIndex !== -1) {
                // Find the end of the slash command in this text node
                let commandEnd = range.startOffset;
                for (let i = range.startOffset; i < textNode.textContent.length; i++) {
                    if (textNode.textContent[i] === ' ' || textNode.textContent[i] === '/' || textNode.textContent[i] === '\n') {
                        commandEnd = i;
                        break;
                    }
                }
                
                // Replace the slash command with the placeholder
                const beforeSlash = textNode.textContent.substring(0, lastSlashIndex);
                const afterCommand = textNode.textContent.substring(commandEnd);
                const placeholder = `{${placeholderKey}}`;
                
                // Create the new text for this node
                const newTextNodeContent = beforeSlash + placeholder + afterCommand;
                
                // Update the text node directly
                textNode.textContent = newTextNodeContent;
                
                // Set cursor position after the inserted placeholder
                const newOffset = beforeSlash.length + placeholder.length;
                range.setStart(textNode, newOffset);
                range.setEnd(textNode, newOffset);
                selection.removeAllRanges();
                selection.addRange(range);
                
                // Trigger onChange to update the value and re-render badges
                setTimeout(() => {
                    const newValue = extractTextWithPlaceholders();
                    emitValue(newValue);
                }, 0);
            } 
        } 
        
        setShowDropdown(false);
    };

    // Handle key events
    const handleKeyDown = (e) => {
        if (showDropdown) {
            if (e.key === 'Escape') {
                setShowDropdown(false);
                e.preventDefault();
            } else if (e.key === 'Enter' && filteredPlaceholders.length > 0) {
                e.preventDefault();
                handlePlaceholderSelect(filteredPlaceholders[0]);
            }
        }
        

    };

    // Handle paste events to prevent duplication of placeholders
    const handlePaste = (e) => {
        e.preventDefault();
        
        // Get the pasted text
        const pastedText = e.clipboardData.getData('text/plain');
        if (!pastedText) return;
        
        // Save cursor position before paste
        const caretOffset = saveCursorPosition();
        
        // Get current selection
        const selection = window.getSelection();
        if (selection.rangeCount === 0) return;
        
        const range = selection.getRangeAt(0);
        
        // Delete any selected content first
        range.deleteContents();
        
        // Insert the pasted text as a single text node
        const textNode = document.createTextNode(pastedText);
        range.insertNode(textNode);
        
        // Clear selection to avoid range issues
        selection.removeAllRanges();
        
        // Update state with the new content
        isUpdatingRef.current = true;
        setTimeout(() => {
            const newValue = extractTextWithPlaceholders();
            emitValue(newValue);
            
            // Wait for DOM to stabilize before restoring cursor
            setTimeout(() => {
                try {
                    // Simple cursor placement at end of content
                    const selection = window.getSelection();
                    const range = document.createRange();
                    
                    if (divRef.current && divRef.current.lastChild) {
                        if (divRef.current.lastChild.nodeType === Node.TEXT_NODE) {
                            range.setStart(divRef.current.lastChild, divRef.current.lastChild.textContent.length);
                            range.setEnd(divRef.current.lastChild, divRef.current.lastChild.textContent.length);
                        } else {
                            range.setStartAfter(divRef.current.lastChild);
                            range.setEndAfter(divRef.current.lastChild);
                        }
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                } catch (error) {
                    console.warn('Paste cursor positioning failed:', error);
                }
                isUpdatingRef.current = false;
            }, 10);
        }, 0);
    };





    // Track if we're updating programmatically to avoid cursor jumping
    const isUpdatingRef = useRef(false);
    const lastValueRef = useRef(value);

    // Update content when value prop changes (but not during typing)
    useEffect(() => {
        // Skip if we're currently processing input to avoid conflicts
        if (isUpdatingRef.current) {
            lastValueRef.current = value;
            return;
        }
        
        // Only rebuild if value truly changed from external source
        const currentExtracted = extractTextWithPlaceholders();
        const shouldRebuild = value !== lastValueRef.current && value !== currentExtracted;
        
        if (divRef.current && shouldRebuild) {
            const caretOffset = saveCursorPosition();
            
            divRef.current.innerHTML = '';
            const content = renderContent(value);
            
            if (Array.isArray(content)) {
                content.forEach(part => {
                    if (typeof part === 'string') {
                        divRef.current.appendChild(document.createTextNode(part));
                    } else {
                        // Create DOM element for React element
                        const span = document.createElement('span');
                        span.style.cssText = Object.entries(part.props.style).map(([key, val]) => 
                            `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${val}`
                        ).join('; ');
                        span.textContent = part.props.children[0]; // displayText
                        span.setAttribute('contenteditable', 'false');
                        span.setAttribute('data-placeholder', part.props['data-placeholder']);
                        span.setAttribute('data-key', part.props['data-key']);
                        span.onclick = () => removePlaceholder(part.props['data-placeholder']);
                        
                        // Add the × button
                        const closeButton = document.createElement('span');
                        closeButton.textContent = '×';
                        closeButton.style.marginLeft = '4px';
                        closeButton.style.fontSize = '0.75rem';
                        span.appendChild(closeButton);
                        
                        divRef.current.appendChild(span);
                    }
                });
            }
            
            // Restore cursor position
            setTimeout(() => {
                restoreCursorPosition(caretOffset);
            }, 0);
        }
        
        lastValueRef.current = value;
    }, [value]);

    const defaultStyle = {
        minHeight: '100px',
        padding: '8px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontFamily: 'inherit',
        fontSize: '14px',
        lineHeight: '1.4',
        outline: 'none',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
        ...style
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            { props.label ? 
                    <label htmlFor={ id } >
                        { props.label }
                    </label> : null }
            <div
                ref={divRef}
                contentEditable
                onBeforeInput={handleBeforeInput}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                style={defaultStyle}
                className={className}
                suppressContentEditableWarning={true}
                {...props}
            >
            </div>
            
            {/* Dropdown */}
            {showDropdown && (
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'absolute',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        minWidth: '200px'
                    }}
                >
                    {filteredPlaceholders.map((key, index) => (
                        <div
                            key={key}
                            onClick={() => handlePlaceholderSelect(key)}
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: index < filteredPlaceholders.length - 1 ? '1px solid #eee' : 'none',
                                backgroundColor: index === 0 ? '#f5f5f5' : 'white'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = index === 0 ? '#f5f5f5' : 'white';
                            }}
                        >
                            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                {flatPlaceholderMap[key]}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                                {key}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TextAreaWithPlaceholders;